"use client";
import React from "react";
import { AbsoluteFill, useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { loadFont } from "@remotion/google-fonts/NotoSansDevanagari";
import { HOLES_DATA_URI } from "./trimbakHolesData";

const { fontFamily } = loadFont("normal", { weights: ["400", "700", "800", "900"], subsets: ["devanagari", "latin"] });

const PAD = 25;
const YELLOW = "#FFD60A";
const WHITE = "#FFFFFF";
const RED = "#FF3B2E";
const SH = "0 5px 14px rgba(0,0,0,0.9), 0 2px 5px rgba(0,0,0,0.95)";
const HOLES = HOLES_DATA_URI;

const BASE_FS = 66, BIG_FS = 92, SMALL_FS = 46;
const STAGGER = 0.16, DROP = 26, DUR = 0.32;
const LH = 1.18;
const LINE_GAP = -10;

type Pos = "TL" | "BL" | "LowerThird";
type Word = { t: string; c?: "y" | "w" | "r"; big?: boolean; small?: boolean };
export type Phrase = { lines: Word[][]; start: number; out: number; pos?: Pos };
const col = (c?: string) => (c === "w" ? WHITE : c === "r" ? RED : YELLOW);

// Part A — Krishna ki mrityu (0–34.60s)
export const PART_A: Phrase[] = [
  // A1 — मामूली तीर से मर गए? — BL (anchor sits in TL area at this moment)
  { start: 3.4, out: 6.5, pos: "BL", lines: [
    [{ t: "मामूली" }, { t: "तीर", c: "w", big: true }, { t: "से", c: "r", small: true }],
    [{ t: "मर" }, { t: "गए?", c: "w", big: true }],
  ]},
  // A2 — रातों-रात / डूब गई — TL
  { start: 7.9, out: 11.0, pos: "TL", lines: [
    [{ t: "रातों-रात" }],
    [{ t: "डूब" }, { t: "गई", c: "w", big: true }],
  ]},
  // A3 — जय श्री कृष्ण! — Lower-1/3
  { start: 33.0, out: 34.6, pos: "LowerThird", lines: [
    [{ t: "जय" }, { t: "श्री" }, { t: "कृष्ण!", c: "w", big: true }],
  ]},
];

// Part B — Krishna ki mrityu (34.60–497.79s). Times are RELATIVE to Part B start (original − 34.60).
// All TL per Yash. खून + लाल in red.
export const PART_B: Phrase[] = [
  // B1 — "हे माधव!" — Gandhari's accusation (orig 63.59–64.33) → 28.99–29.73 → window 28.8–31.0
  { start: 28.8, out: 31.0, pos: "TL", lines: [
    [{ t: "\"हे", c: "r", big: true }, { t: "माधव!\"", c: "r", big: true }],
  ]},
  // B2 — छत्तीस साल बाद — orig 70.95–71.96 → 36.35–37.36
  { start: 36.0, out: 39.0, pos: "TL", lines: [
    [{ t: "छत्तीस", c: "w", big: true }],
    [{ t: "साल" }, { t: "बाद" }],
  ]},
  // B3 — लोहे का मूसल — orig 135.18–135.97 → 100.58–101.37
  { start: 100.3, out: 103.0, pos: "TL", lines: [
    [{ t: "लोहे" }, { t: "का", c: "r", small: true }],
    [{ t: "मूसल", c: "w", big: true }],
  ]},
  // B4 — 'एराका' घास — orig 168.00–170.46 → 133.40–135.86
  { start: 133.2, out: 136.5, pos: "TL", lines: [
    [{ t: "'एराका'", c: "w", big: true }],
    [{ t: "घास" }],
  ]},
  // NEW — खून से लाल — orig 217.98–220.5 → 183.38–185.90 (rेत खून से / लाल हो गई)
  { start: 183.2, out: 186.2, pos: "TL", lines: [
    [{ t: "रेत" }, { t: "खून", c: "r", big: true }, { t: "से", c: "r", small: true }],
    [{ t: "लाल", c: "r", big: true }, { t: "हो" }, { t: "गई" }],
  ]},
  // B5 — स्वयं शेषनाग — orig 244.61–245.74 → 210.01–211.14
  { start: 209.8, out: 212.5, pos: "TL", lines: [
    [{ t: "स्वयं" }],
    [{ t: "शेषनाग", c: "w", big: true }],
  ]},
  // B6 — "मुझसे यह क्या पाप हो गया!" — orig 312.11–314.63 → 277.51–280.03
  { start: 277.5, out: 281.0, pos: "TL", lines: [
    [{ t: "\"मुझसे", c: "r", big: true }, { t: "यह", c: "r", small: true }],
    [{ t: "क्या", c: "r", big: true }, { t: "पाप", c: "r", big: true }],
    [{ t: "हो", c: "r" }, { t: "गया!\"", c: "r", big: true }],
  ]},
  // B7 — बालि का पुनर्जन्म — orig 338.20–339.84 → 303.60–305.24
  { start: 303.4, out: 306.5, pos: "TL", lines: [
    [{ t: "बालि" }, { t: "का", c: "r", small: true }],
    [{ t: "पुनर्जन्म", c: "w", big: true }],
  ]},
  // B8 — द्वारका डूब गई (was निगल गया, changed per Yash) — orig 391.29–393.68 → 356.69–359.08
  { start: 356.4, out: 360.0, pos: "TL", lines: [
    [{ t: "द्वारका" }],
    [{ t: "डूब", c: "w", big: true }, { t: "गई", c: "w", big: true }],
  ]},
  // B9 — 16 मीटर नीचे — orig 399.50–401.08 → 364.90–366.48
  { start: 364.7, out: 367.5, pos: "TL", lines: [
    [{ t: "16", c: "w", big: true }, { t: "मीटर", c: "w", big: true }],
    [{ t: "नीचे" }],
  ]},
  // B10 — चमकती चीज़ — orig 442.04–442.98 → 407.44–408.38
  { start: 407.2, out: 410.0, pos: "TL", lines: [
    [{ t: "चमकती" }],
    [{ t: "चीज़", c: "w", big: true }],
  ]},
  // B11 — जय श्री कृष्ण! — orig 496.42–497.79 → 461.82–463.19
  { start: 461.6, out: 463.19, pos: "LowerThird", lines: [
    [{ t: "जय" }, { t: "श्री" }, { t: "कृष्ण!", c: "w", big: true }],
  ]},
];

const PHRASES = PART_A;

const posStyle = (pos: Pos): React.CSSProperties => {
  if (pos === "TL")        return { top: PAD, left: PAD,    alignItems: "flex-start" };
  if (pos === "BL")        return { bottom: PAD, left: PAD, alignItems: "flex-start" };
  // LowerThird = horizontally centred, ~70% down
  return { left: 0, right: 0, top: "70%", alignItems: "center" };
};

const PhraseBlock: React.FC<{ p: Phrase; fps: number }> = ({ p, fps }) => {
  const frame = useCurrentFrame();
  const t = frame / fps;
  const phraseOp = interpolate(t, [p.out - 0.4, p.out], [1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  if (t < p.start - 0.1 || t > p.out + 0.1) return null;
  const pos = p.pos ?? "TL";
  let gi = -1;
  return (
    <div style={{ position: "absolute", ...posStyle(pos), fontFamily, opacity: phraseOp, display: "flex", flexDirection: "column" }}>
      {p.lines.map((line, li) => (
        <div key={li} style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "baseline",
          justifyContent: pos === "LowerThird" ? "center" : "flex-start",
          gap: "0 16px",
          marginTop: li === 0 ? 0 : LINE_GAP,
          padding: pos === "LowerThird" ? "0 25px" : 0,
        }}>
          {line.map((w, wi) => {
            gi += 1;
            const aT = p.start + gi * STAGGER;
            const k = Math.min(Math.max((t - aT) / DUR, 0), 1);
            const ease = 1 - Math.pow(1 - k, 3);
            const op = interpolate(t, [aT, aT + 0.18], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
            const y = -DROP * (1 - ease);
            return (
              <span key={wi} style={{
                display: "inline-block",
                transform: `translateY(${y}px)`,
                opacity: op,
                color: col(w.c),
                fontSize: w.big ? BIG_FS : w.small ? SMALL_FS : BASE_FS,
                fontWeight: w.big ? 900 : 800,
                lineHeight: LH,
                letterSpacing: "0.005em",
                textShadow: SH,
              }}>{w.t}</span>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export const KrishnaOverlays: React.FC<{ phrases?: Phrase[] }> = ({ phrases = PHRASES }) => {
  const { fps } = useVideoConfig();
  return (
    <AbsoluteFill style={{
      backgroundColor: "transparent",
      WebkitMaskImage: `url(${HOLES})`,
      maskImage: `url(${HOLES})`,
      WebkitMaskSize: "1280px 704px",
      maskSize: "1280px 704px",
      WebkitMaskRepeat: "no-repeat",
      maskRepeat: "no-repeat",
      WebkitMaskPosition: "0 0",
      maskPosition: "0 0",
    }}>
      {phrases.map((p, i) => <PhraseBlock key={i} p={p} fps={fps} />)}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
